import { type Ref, ref } from 'vue'
import { defineStore } from 'pinia'
import { DateTime } from 'ts-luxon'
import type { ObjectsListItemMap } from '@/entities/Object'

export const useMapSelect = defineStore('map_select', () => {
  const isSelect = ref(false)
  const isDisabled = ref(false)
  let startMapCoords = [0, 0]
  const emits = ref<Function | null>(null)
  let timer = 0

  /**
   * Функция обработчик получения стартовых координат на карте
   * @param _
   * @param event
   */
  const startSelectHandler = (_, event: any, map: any) => {
    timer = DateTime.now().toMillis()

    if (event.details.shiftKey) {
      startMapCoords = event.coordinates
      isSelect.value = true

      /* Убираем возможность перемещения карты */
      let bh = map.value.entity.behaviors
      bh = bh.filter((item: string) => item !== 'drag')

      map.value.entity.setBehaviors(bh)
    }
  }

  /**
   * Функция обработчик получения конечных координат на карте
   * @param _
   * @param event
   */
  const endSelectHandler = (_, event: any, map: any) => {
    const timeout = DateTime.now().toMillis() - timer > 300

    if (isSelect.value && timeout) {
      if (emits.value) emits.value('selected', [startMapCoords, event.coordinates])
    }

    isSelect.value = false

    /* Возвращаем возможность перемещения карты */
    let bh = map.value.entity.behaviors
    map.value.entity.setBehaviors([...bh, 'drag'])
  }

  /**
   * Функция выделения объекта по клику
   * @param object - объект выделения
   * @param objects - реактивный массив выделенных объектов
   * @param event
   */
  const selectObjectToClick = (
    object: ObjectsListItemMap,
    objects: Ref<ObjectsListItemMap[]>,
    event: any,
  ) => {
    if (event.details.shiftKey) {
      if (!objects.value.some((item) => item.id === object.id)) {
        objects.value = [...objects.value, object]
      } else {
        objects.value = objects.value.filter((item) => item.id !== object.id)
      }
    } else {
      objects.value = [object]
    }
  }

  /**
   * Функция выделения объекта по клику по кластеру
   * @param objectsCluster
   * @param objects
   * @param event
   */
  const selectObjectToClusterClick = (
    objectsCluster: ObjectsListItemMap[],
    objects: Ref<ObjectsListItemMap[]>,
    event: any,
  ) => {
    if (event.details.shiftKey) {
      objectsCluster.forEach((object) => selectObjectToClick(object, objects, event))
    } else {
      objects.value = objectsCluster
    }
  }

  /**
   * Функция выделения объектов по выделенной области
   * @param objects - реактивный массив выделенных объектов
   * @param listObjects - список объектов расположенных на карте
   * @param coords - координаты выделенной области
   */
  const selectObjectToSelected = (
    objects: Ref<ObjectsListItemMap[]>,
    listObjects: ObjectsListItemMap[],
    coords: number[][],
  ) => {
    const maxLn = Math.max(coords[0][0], coords[1][0])
    const minLn = Math.min(coords[0][0], coords[1][0])
    const maxLt = Math.max(coords[0][1], coords[1][1])
    const minLt = Math.min(coords[0][1], coords[1][1])

    const selectedObjectLocal: ObjectsListItemMap[] = listObjects.filter(
      (object) =>
        object.coords[0][1] > minLn &&
        object.coords[0][1] < maxLn &&
        object.coords[0][0] > minLt &&
        object.coords[0][0] < maxLt,
    )

    const selectedObjectLocalIds = selectedObjectLocal.map((item) => item.id)
    const selectedObjectIds = objects.value.map((item) => item.id)
    const selectedObjectIdsSet = new Set(selectedObjectIds)

    if (selectedObjectLocalIds.every((id) => selectedObjectIdsSet.has(id))) {
      objects.value = objects.value.filter((item) => !selectedObjectLocalIds.includes(item.id))
    } else {
      objects.value = [
        ...objects.value,
        ...selectedObjectLocal.filter((item) => !selectedObjectIdsSet.has(item.id)),
      ]
    }
  }

  return {
    isSelect,
    isDisabled,
    emits,

    startSelectHandler,
    endSelectHandler,
    selectObjectToClick,
    selectObjectToSelected,
    selectObjectToClusterClick,
  }
})
